import { useCallback } from 'react'

import { FormTemplateMappingSource, FormTemplateMappingTarget } from '../../../models/FormTemplate'

import { useUploadFileMutation, useUploadFileRequestMutation } from '../../../store/media/file'
import { useCreateFormTemplateMutation } from '../../../store/ai/formTemplate'
import { useUpdateFileMutation } from '../../../store/media/media'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'

import { FileKind, FileStatus } from '../../../models/File'

export const useCreateFormTemplate = () => {
  const accountId = useAppSelector(selectAccountId)

  const [triggerCreateFormTemplate] = useCreateFormTemplateMutation()
  const [triggerUploadFileRequest] = useUploadFileRequestMutation()
  const [triggerUploadFile] = useUploadFileMutation()
  const [triggerUpdateFile] = useUpdateFileMutation()

  const requestFileUpload = useCallback(
    async (file: File) => {
      const body = { fileName: file.name, fileSize: file.size, contentType: file.type, kind: FileKind.FORM_TEMPLATE }
      const { data: fileUploadRequestResponse } = await triggerUploadFileRequest({ accountId, body }).unwrap()
      return {
        uploadUrl: fileUploadRequestResponse.fileUploadUrl,
        uploadParameters: fileUploadRequestResponse.fileUploadParameters,
        fileInfo: fileUploadRequestResponse.fileInfo,
      }
    },
    [accountId, triggerUploadFileRequest],
  )

  const uploadFile = useCallback(
    async (file: File) => {
      try {
        const { uploadUrl, uploadParameters, fileInfo } = await requestFileUpload(file)
        const { fileId } = fileInfo
        await triggerUploadFile({ uploadUrl, uploadParameters, file }).unwrap()
        await triggerUpdateFile({ accountId, fileId, body: { status: FileStatus.UPLOADED } }).unwrap()
        return fileInfo
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, requestFileUpload, triggerUpdateFile, triggerUploadFile],
  )

  const createFormTemplate = useCallback(
    async (fileId: string, name: string, scorecardId: string, targets: FormTemplateMappingTarget[], mapping: Record<string, FormTemplateMappingSource>) => {
      try {
        const { data: formTemplate } = await triggerCreateFormTemplate({ accountId, name, scorecardId, fileId, targets, mapping })
        if (!formTemplate) {
          return null
        }
        return formTemplate
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, triggerCreateFormTemplate],
  )

  return { uploadFile, createFormTemplate }
}
